import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { FiShoppingCart, FiHeart, FiCheck, FiChevronRight, FiStar, FiPackage, FiTag } from 'react-icons/fi';
import { productAPI, categoryAPI } from '../api/services';
import { useCartStore } from '../store/cartStore';
import { useWishlistStore } from '../store/wishlistStore';
import { useProductOverride } from '../context/ProductPriceContext';
import ReviewSection from '../components/ReviewSection';
import './ProductDetail.css';

function ProductDetail() {
  const { id } = useParams();
  const [product,   setProduct]  = useState(null);
  const [category,  setCategory] = useState(null);
  const [quantity,  setQuantity] = useState(1);
  const [loading,   setLoading]  = useState(true);
  const [added,     setAdded]    = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const [showAllVariants, setShowAllVariants] = useState(false);
  const navigate = useNavigate();
  const { addToCart } = useCartStore();
  const { toggle, isLiked } = useWishlistStore();

  useEffect(() => {
    setLoading(true);
    setProduct(null);
    setCategory(null);
    setShowAllVariants(false);
    setActiveImg(0);
    productAPI.getProduct(id)
      .then(r => {
        setProduct(r.data);
        if (r.data.category_id) {
          categoryAPI.getCategories()
            .then(cr => {
              const cat = cr.data.find(c => c.id === r.data.category_id);
              setCategory(cat || null);
            })
            .catch(() => {});
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  const handleAddToCart = async () => {
    try {
      await addToCart(product.id, quantity, product);
      setAdded(true);
      setTimeout(() => setAdded(false), 1800);
    } catch {
      alert('Failed to add to cart');
    }
  };

  const handleBuyNow = async () => {
    try { await addToCart(product.id, quantity, product); } catch {}
    navigate('/checkout');
  };

  const override = useProductOverride(product?.id);

  if (loading) return (
    <div className="pd-loading">
      <div className="pd-skeleton-img" />
      <div className="pd-skeleton-info">
        <div className="pd-skeleton-line w30" />
        <div className="pd-skeleton-line w80" />
        <div className="pd-skeleton-line w40" />
        <div className="pd-skeleton-line w60" />
        <div className="pd-skeleton-line w90" />
      </div>
    </div>
  );

  if (!product) return (
    <div className="container" style={{ padding: '80px 0', textAlign: 'center' }}>
      <p style={{ color: '#64748b' }}>Product not found.</p>
      <Link to="/products" style={{ color: 'var(--primary)', fontWeight: 700 }}>Back to Products</Link>
    </div>
  );

  const p = override ? { ...product, ...override } : product;
  const activeDiscount = p.discount_price && p.discount_active !== false ? p.discount_price : null;
  const price    = activeDiscount || p.price;
  const hasDisc  = !!activeDiscount;
  const discount = hasDisc ? Math.round(((p.price - activeDiscount) / p.price) * 100) : 0;
  const liked    = isLiked(product.id);
  const inStock  = product.stock > 0;

  const images = [product.image_url, product.image_url_2, product.image_url_3].filter(Boolean);

  return (
    <div className="pd-page">
      <div className="container">

        {/* Breadcrumb */}
        <nav className="pd-breadcrumb">
          <Link to="/">Home</Link>
          <FiChevronRight size={12} />
          <Link to="/products">Products</Link>
          {category && (
            <>
              <FiChevronRight size={12} />
              <Link to={`/products?category_id=${category.id}`}>{category.name}</Link>
            </>
          )}
          <FiChevronRight size={12} />
          <span>{product.name}</span>
        </nav>

        <div className="pd-layout">

          {/* ── Image panel ── */}
          <div className="pd-img-panel">
            {/* Thumbnail strip — left side */}
            {images.length > 1 && (
              <div className="pd-thumb-strip">
                {images.map((img, i) => (
                  <button
                    key={i}
                    className={`pd-thumb-btn ${activeImg === i ? 'active' : ''}`}
                    onClick={() => setActiveImg(i)}
                  >
                    <img src={img} alt={`${product.name} ${i + 1}`} />
                  </button>
                ))}
              </div>
            )}

            {/* Main image */}
            <div className="pd-img-wrap">
              {hasDisc && <div className="pd-badge">−{discount}%</div>}
              <button
                className={`pd-wish-btn ${liked ? 'liked' : ''}`}
                onClick={() => toggle(product)}
                aria-label="Wishlist"
              >
                <FiHeart size={18} />
              </button>
              <img
                src={images[activeImg] || 'https://placehold.co/500x500?text=?'}
                alt={product.name}
                className="pd-img"
              />
            </div>
          </div>

          {/* ── Info panel ── */}
          <div className="pd-info">

            {/* Category tag */}
            {product.category?.name && (
              <div className="pd-meta-row">
                <span className="pd-cat-tag">
                  <FiTag size={11} />
                  {product.category.name}
                </span>
              </div>
            )}

            <h1 className="pd-name">{product.name}</h1>

            {/* Rating */}
            <div className="pd-rating">
              <div className="pd-stars">
                {[1,2,3,4,5].map(s => (
                  <FiStar
                    key={s}
                    size={15}
                    className={s <= Math.round(product.rating) ? 'star-filled' : 'star-empty'}
                  />
                ))}
              </div>
              <span className="pd-rating-val">{product.rating.toFixed(1)}</span>
              <span className="pd-rating-sep">·</span>
              <span className="pd-rating-count">{product.reviews_count} reviews</span>
            </div>

            {/* Price */}
            <div className="pd-price-row">
              <span className="pd-price">Rs {price.toFixed(0)}<span className="pd-slash">/-</span></span>
              {hasDisc && <span className="pd-orig">Rs {product.price.toFixed(0)}/-</span>}
              {hasDisc && <span className="pd-save-pill">Save {discount}%</span>}
            </div>
            {inStock && <span className="pd-stock-note">{product.stock} in stock</span>}

            {/* Divider */}
            <div className="pd-divider" />

            {/* Actions */}
            {inStock && (
              <div className="pd-actions">
                <div className="pd-qty">
                  <button className="pd-qty-btn" onClick={() => setQuantity(q => Math.max(1, q - 1))}>−</button>
                  <span className="pd-qty-val">{quantity}</span>
                  <button className="pd-qty-btn" onClick={() => setQuantity(q => Math.min(product.stock, q + 1))}>+</button>
                </div>
                <button className="pd-buy-btn" onClick={handleBuyNow}>
                  Buy Now
                </button>
                <button
                  className={`pd-cart-btn ${added ? 'added' : ''}`}
                  onClick={handleAddToCart}
                >
                  {added
                    ? <><FiCheck size={18} /> Added</>
                    : <><FiShoppingCart size={18} /> Add to Cart</>
                  }
                </button>
              </div>
            )}

            {!inStock && (
              <button className="pd-cart-btn pd-oos" disabled>
                <FiPackage size={18} /> Out of Stock
              </button>
            )}

            {/* Variants */}
            {product.variants && product.variants.length > 0 && (
              <div className="pd-variants-block">
                <p className="pd-variants-label">Available Variants ({product.variants.length + 1})</p>
                <div className="pd-variants-list">
                  <div className="pd-variant-item pd-variant-current">
                    <img src={product.image_url} alt={product.name} className="pd-variant-img" />
                    <span className="pd-variant-name">{product.name}</span>
                    <span className="pd-variant-price">Rs {((product.discount_price && product.discount_active !== false ? product.discount_price : null) || product.price).toFixed(0)}</span>
                  </div>
                  {(showAllVariants ? product.variants : product.variants.slice(0, 4)).map(v => (
                    <Link key={v.id} to={`/products/${v.id}`} className="pd-variant-item">
                      <img src={v.image_url} alt={v.name} className="pd-variant-img" />
                      <span className="pd-variant-name">{v.name}</span>
                      <span className="pd-variant-price">Rs {((v.discount_price && v.discount_active !== false ? v.discount_price : null) || v.price).toFixed(0)}</span>
                    </Link>
                  ))}
                </div>
                {product.variants.length > 4 && (
                  <button className="pd-var-show-more" onClick={() => setShowAllVariants(v => !v)}>
                    {showAllVariants ? '▲ Show less' : `+ ${product.variants.length - 4} more variants`}
                  </button>
                )}
              </div>
            )}

          </div>
        </div>

        {/* Description */}
        {product.description && (
          <div className="pd-desc-block">
            <h2 className="pd-desc-label">Product details of {product.name}</h2>
            <p className="pd-desc">{product.description}</p>
          </div>
        )}

        {/* Reviews */}
        <ReviewSection productId={id} productName={product.name} />
      </div>
    </div>
  );
}

export default ProductDetail;
